import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forwardRef, ReactNode, useImperativeHandle } from 'react';

import { isHttpErrorResponse } from '../../lib/http';
import { useHttpClient } from '../../lib/http-client';

interface UserContentDismissalRenderProps {
	name: string;
	namespace: string;
	children: (updateUserContentDismissal: VoidFunction) => ReactNode;
}

export interface UserContentDismissalRef {
	dismiss: () => void;
}

/**
 * In order for the `useUserContentDismissal` hook to use `useQueryClient`
 * this component is wrapped in a `QueryClientProvider` to ensure an instance is
 * always available
 *
 * It also reduces the amount of dependencies required in order to import
 * and use this component
 */
export const UserContentDismissal = forwardRef<UserContentDismissalRef, UserContentDismissalRenderProps>(
	({ name, namespace, children }: UserContentDismissalRenderProps, ref) => {
		useImperativeHandle(ref, () => ({
			dismiss: () => {
				updateUserContentDismissal();
			},
		}));
		const { loading, data, error, updateUserContentDismissal } = useUserContentDismissal({
			name,
			namespace,
		});

		if (loading) {
			return null;
		}

		if (error || !data || !data.dismissed_at) {
			return <>{children(updateUserContentDismissal)}</>;
		} else {
			return null;
		}
	}
);

UserContentDismissal.displayName = 'UserContentDismissal';

interface UseUserContentDismissalProps {
	name: string;
	namespace: string;
	/**
	 * Whether to enable the query. When false, the query will not run.
	 * Useful for conditionally fetching dismissal state only when needed.
	 * @default true
	 */
	enabled?: boolean;
}

interface UserContentDismissal {
	id: string;
	name: string;
	namespace: string;
	dismissed_at: string;
}

interface UserContentDismissalResponse {
	result: UserContentDismissal[] | null;
	success: boolean;
}

export const useUserContentDismissal = ({ name, namespace, enabled = true }: UseUserContentDismissalProps) => {
	const http = useHttpClient();
	const QUERY_KEY = ['userContentDismissals', namespace, name];

	// queryClient must be initialized by the parent component importing this hook
	const queryClient = useQueryClient();

	const { data, error, isLoading } = useQuery(
		QUERY_KEY,
		() =>
			http.get<UserContentDismissalResponse>('/user_content_dismissals', {
				params: {
					name,
					namespace,
				},
			}),
		{
			select: (response) => response.data,
			refetchInterval: Infinity,
			enabled,
		}
	);

	const userContentDismissalData = data?.result?.[0] || null;
	// Only report error if query was enabled and actually ran
	const requestError = enabled && !isLoading && (error || !userContentDismissalData);

	const update = useMutation<void, Error, void, { previous: unknown }>(
		async () => {
			try {
				await http.post('/user_content_dismissals', {
					user_content_dismissal: {
						name,
						namespace,
					},
				});
			} catch (error) {
				// 409 = record already exists (e.g. duplicate dismiss); treat as success
				if (isHttpErrorResponse(error) && error.response.status === 409) {
					return;
				}
				throw error;
			}
		},
		{
			// Optimistically mark as dismissed so dependent UI (e.g. interrupting promos)
			// unmounts immediately instead of lingering for the POST + refetch round-trip.
			onMutate: async () => {
				await queryClient.cancelQueries(QUERY_KEY);
				const previous = queryClient.getQueryData(QUERY_KEY);
				const optimisticDismissal: UserContentDismissal = {
					id: 'optimistic',
					name,
					namespace,
					dismissed_at: new Date().toISOString(),
				};
				queryClient.setQueryData(QUERY_KEY, (old: unknown) => {
					const response = (old ?? {}) as { data?: UserContentDismissalResponse };
					return {
						...response,
						data: { success: true, result: [optimisticDismissal] } satisfies UserContentDismissalResponse,
					};
				});
				return { previous };
			},
			onError: (error, _variables, context) => {
				if (context) {
					queryClient.setQueryData(QUERY_KEY, context.previous);
				}
				console.error(error);
			},
			onSettled: () => {
				queryClient.invalidateQueries(QUERY_KEY);
			},
		}
	);

	const updateUserContentDismissal = update.mutate;
	const updateUserContentDismissalAsync = update.mutateAsync;

	return {
		loading: enabled ? isLoading : false,
		data: userContentDismissalData,
		error: requestError,
		updateUserContentDismissal,
		updateUserContentDismissalAsync,
	};
};
